// Title Card Overlay for Remotion Podcast Composition
// Shows at the start of the video with episode title, description, and hosts

import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from "remotion";

const MEDD_ACCENT = "#2DD4BF";

interface PodcastTitleProps {
  title: string;
  description?: string;
  host1Name: string;
  host2Name: string;
}

export const PodcastTitle: React.FC<PodcastTitleProps> = ({
  title,
  description,
  host1Name,
  host2Name,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentTimeSec = frame / fps;

  // Staggered entrance animations
  const titleEntry = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 60 },
    delay: 5,
  });

  const descEntry = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 60 },
    delay: 15,
  });

  const hostsEntry = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 60 },
    delay: 25,
  });

  // Fade out at the end
  const fadeOut = interpolate(currentTimeSec, [2.5, 3.5], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        padding: "60px 80px",
        opacity: fadeOut,
        zIndex: 10,
      }}
    >
      {/* MEDD Logo */}
      <div
        style={{
          opacity: titleEntry,
          transform: `translateY(${interpolate(titleEntry, [0, 1], [-20, 0])}px)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: 5,
            textTransform: "uppercase",
          }}
        >
          medd.
        </span>
        <span
          style={{
            color: MEDD_ACCENT,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 3,
            textTransform: "uppercase",
            marginTop: -4,
          }}
        >
          Podcast
        </span>
      </div>

      {/* Divider line */}
      <div
        style={{
          width: interpolate(titleEntry, [0, 1], [0, 120]),
          height: 2,
          backgroundColor: MEDD_ACCENT,
          borderRadius: 1,
          opacity: 0.6,
        }}
      />

      {/* Title */}
      <div
        style={{
          opacity: titleEntry,
          transform: `translateY(${interpolate(titleEntry, [0, 1], [30, 0])}px)`,
          color: "white",
          fontSize: 38,
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.3,
          maxWidth: 900,
        }}
      >
        {title}
      </div>

      {/* Description */}
      {description && (
        <div
          style={{
            opacity: descEntry,
            transform: `translateY(${interpolate(descEntry, [0, 1], [20, 0])}px)`,
            color: "rgba(255,255,255,0.6)",
            fontSize: 18,
            textAlign: "center",
            maxWidth: 700,
            lineHeight: 1.5,
          }}
        >
          {description.length > 150
            ? description.slice(0, 147) + "..."
            : description}
        </div>
      )}

      {/* Hosts */}
      <div
        style={{
          opacity: hostsEntry,
          transform: `translateY(${interpolate(hostsEntry, [0, 1], [20, 0])}px)`,
          display: "flex",
          alignItems: "center",
          gap: 20,
          marginTop: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#3B82F6",
            }}
          />
          <span
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {host1Name}
          </span>
        </div>
        <span
          style={{
            color: "rgba(255,255,255,0.3)",
            fontSize: 14,
          }}
        >
          &
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#10B981",
            }}
          />
          <span
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {host2Name}
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default PodcastTitle;
